import { API_CONFIG, REQUEST_CONFIG } from '../config/api.config';
import type { ApiResponse } from '../types/api.types';
import {
  createApiError,
  retryWithBackoff,
  AuthenticationError,
  NetworkError,
  CorsError,
  TimeoutError,
} from '../utils/errors';
import i18n from '../../i18n';
import { useAuthStore } from '../../features/auth/store/authStore';
import { navigateToLogin, isOnAuthPage } from '../utils/navigation';
import {
  showCorsErrorNotification,
  showTimeoutErrorNotification,
  showNetworkErrorNotification,
} from '../utils/notifications';
import { now, timeDifference } from '../utils';

// Lazy import to avoid circular dependency
type GCPAuthServiceInstance =
  (typeof import('../../features/auth/services/GCPAuthService'))['authService'];

async function getAuthService(): Promise<GCPAuthServiceInstance> {
  const module = await import('../../features/auth/services/GCPAuthService');
  return module.authService;
}

/**
 * In-memory CSRF token fetched from the backend's /auth/csrf/ endpoint.
 * In cross-origin deployments the csrftoken cookie belongs to the backend
 * domain, so document.cookie on the frontend domain cannot read it.
 * We fetch the token via the response body instead.
 */
/**
 * Convert JSON integer values with 16+ digits to quoted strings before parsing.
 * Integers larger than Number.MAX_SAFE_INTEGER (2^53-1, a 16-digit number) are
 * silently corrupted by JSON.parse due to IEEE 754 float precision limits.
 * Django document IDs migrated from MongoDB can be 17-18 digits and hit this.
 *
 * Must skip over JSON string contents — datastore identifiers like
 * "google-drive:1152921037415273253" contain a `:` followed by 16+ digits
 * inside a string, and a naive regex would corrupt the JSON.
 */
export function safeBigInts(json: string): string {
  let out = '';
  let inString = false;
  let escaped = false;

  for (let i = 0; i < json.length; i++) {
    const c = json[i];

    if (inString) {
      out += c;
      if (escaped) escaped = false;
      else if (c === '\\') escaped = true;
      else if (c === '"') inString = false;
      continue;
    }

    if (c === '"') {
      inString = true;
      out += c;
      continue;
    }

    if (c !== ':') {
      out += c;
      continue;
    }

    out += c;
    let j = i + 1;
    while (j < json.length && /\s/.test(json[j])) j++;
    const numStart = j;
    if (json[j] === '-') j++;
    const digitsStart = j;
    while (j < json.length && json[j] >= '0' && json[j] <= '9') j++;
    const digitsLen = j - digitsStart;
    const next = json[j];
    const isPureInt = next !== '.' && next !== 'e' && next !== 'E';

    if (digitsLen >= 16 && isPureInt) {
      out += json.slice(i + 1, numStart);
      out += '"' + json.slice(numStart, j) + '"';
      i = j - 1;
    }
  }

  return out;
}

let _csrfToken: string | null = null;
let _csrfFetchPromise: Promise<string | null> | null = null;

/**
 * Return the cached CSRF token, falling back to document.cookie for
 * same-origin (local dev) setups where the cookie IS readable.
 */
export function getCsrfToken(): string | null {
  if (_csrfToken) return _csrfToken;

  // Fallback: same-origin dev where the cookie is directly readable
  const name = 'csrftoken';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

/**
 * Fetch the CSRF token from the backend and cache it in memory.
 * Deduplicates concurrent calls so only one request is made.
 */
export async function fetchCsrfToken(): Promise<string | null> {
  if (_csrfToken) return _csrfToken;

  if (_csrfFetchPromise) return _csrfFetchPromise;

  _csrfFetchPromise = (async () => {
    try {
      const response = await fetch(`${API_CONFIG.BACKEND_API_URL}/auth/csrf/`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        _csrfToken = data.csrfToken ?? null;
      }
    } catch {
      // Non-fatal — getCsrfToken() will still try the cookie fallback
    } finally {
      _csrfFetchPromise = null;
    }
    return _csrfToken;
  })();

  return _csrfFetchPromise;
}

/** Clear the cached CSRF token (e.g. on logout). */
export function clearCsrfToken(): void {
  _csrfToken = null;
}

export interface RequestOptions extends RequestInit {
  timeout?: number;
  isRetry?: boolean;
}

type QueuedRequest<T = unknown> = {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  execute: () => Promise<T>;
};

export class HttpClient {
  private userId: string | null = null;
  private csrfToken: string | null = null;
  private requestQueue: QueuedRequest<unknown>[] = [];
  private isRefreshing = false;
  private activeControllers: Set<AbortController> = new Set();
  private hasShownSessionExpired = false;
  private readonly USER_ID_STORAGE_KEY = 'bot-unify-user-email';

  constructor() {
    // Initialize userId from localStorage on instantiation
    this.userId = localStorage.getItem(this.USER_ID_STORAGE_KEY);
  }

  /**
   * Signal that a new auth session has started (cookie set by backend).
   * Resets session-expired UI state.
   */
  resetSessionState() {
    this.hasShownSessionExpired = false;
  }

  setUserId(userId: string | null) {
    this.userId = userId;

    // Persist to localStorage
    if (userId) {
      localStorage.setItem(this.USER_ID_STORAGE_KEY, userId);
    } else {
      localStorage.removeItem(this.USER_ID_STORAGE_KEY);
    }
  }

  /**
   * Fetch CSRF token from backend and store in memory.
   * Required because the csrftoken cookie is on api.agentlinc.ai and is
   * not readable by JS running on app.agentlinc.ai (cross-subdomain).
   */
  async fetchCsrfToken(): Promise<void> {
    try {
      const response = await fetch(`${API_CONFIG.BACKEND_API_URL}/auth/csrf/`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.csrfToken) {
          this.csrfToken = data.csrfToken;
        }
      }
    } catch {
      // Non-fatal — requests will fall back to cookie-based token
    }
  }

  private showSessionExpiredNotification(): void {
    // Only show notification once per session to avoid spam
    if (this.hasShownSessionExpired) return;
    this.hasShownSessionExpired = true;

    // Check if we're not already on the login page to prevent redirect loops
    if (isOnAuthPage()) {
      return;
    }

    // Navigate to login after a brief delay to allow UI to update
    setTimeout(() => {
      navigateToLogin(true);
    }, 1500);

    // You can integrate with your toast/notification system here
    // For now, using console warning as placeholder
  }

  private shouldRefreshToken(): boolean {
    const { expiresAt, isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated || !expiresAt) return false;

    // Refresh if less than 5 minutes remaining
    const bufferTime = 5 * 60 * 1000; // 5 minutes
    const shouldRefresh = now() > expiresAt - bufferTime;

    if (shouldRefresh) {
      // Token refresh will be handled by the calling method
      // This is intentionally empty as the actual refresh logic is in refreshTokenWithQueue()
    }

    return shouldRefresh;
  }

  private async refreshTokenWithQueue(): Promise<void> {
    if (this.isRefreshing) {
      // Already refreshing, just wait
      return;
    }

    this.isRefreshing = true;

    try {
      // Use retryWithBackoff for resilient token refresh
      await retryWithBackoff(
        async () => {
          const authService = await getAuthService();
          const newToken = await authService.refreshToken();
          if (newToken) {
            // Update the auth store with the new token
            const { setTokenWithExpiry } = useAuthStore.getState();
            // Calculate expiry in seconds (backend may provide it in seconds or as ISO string)
            let expiresIn = 604800; // Default 7 days in seconds
            if (newToken.expiresAt) {
              const expiryDate = new Date(newToken.expiresAt);
              if (!isNaN(expiryDate.getTime())) {
                expiresIn = Math.floor(timeDifference(expiryDate) / 1000);
              }
            }
            setTokenWithExpiry(newToken.token, expiresIn);
            this.resetSessionState();
          } else {
            throw new AuthenticationError('Failed to refresh token');
          }
        },
        {
          maxRetries: 3,
          initialDelay: 1000,
          shouldRetry: error => {
            // Retry on network errors or 5xx errors
            if (error instanceof AuthenticationError) {
              return false; // Don't retry auth errors
            }
            return true;
          },
        }
      );

      // Process queued requests
      const queue = [...this.requestQueue];
      this.requestQueue = [];

      // Execute all queued requests
      await Promise.all(
        queue.map(async request => {
          try {
            const result = await request.execute();
            request.resolve(result);
          } catch (error) {
            request.reject(
              error instanceof Error ? error : new Error(String(error))
            );
          }
        })
      );
    } catch (error) {
      console.error('[HttpClient] Token refresh failed:', error);

      // Reject all queued requests
      const queue = [...this.requestQueue];
      this.requestQueue = [];
      queue.forEach(request => {
        request.reject(new Error(i18n.t('errors.auth.sessionExpired')));
      });

      // Trigger authentication error handler which will clear state and redirect
      useAuthStore.getState().handleAuthenticationError();

      // Show notification to user
      this.showSessionExpiredNotification();

      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  private async handle401AndRetry<T>(
    url: string,
    options: RequestOptions
  ): Promise<ApiResponse<T>> {
    // Don't retry if this is already a retry
    if (options.isRetry) {
      // Final 401 after retry means authentication is truly failed
      console.error('[HttpClient] Authentication failed after retry');

      // Trigger full logout and redirect
      useAuthStore.getState().handleAuthenticationError();
      this.showSessionExpiredNotification();

      throw new AuthenticationError(i18n.t('errors.auth.failed'));
    }

    // Queue this request
    return new Promise<ApiResponse<T>>((resolve, reject) => {
      this.requestQueue.push({
        resolve: resolve as (value: unknown) => void,
        reject,
        execute: () => this.request<T>(url, { ...options, isRetry: true }),
      });

      // Start refresh if not already in progress
      if (!this.isRefreshing) {
        this.refreshTokenWithQueue().catch(error => {
          console.error(
            '[HttpClient] Token refresh failed in 401 handler:',
            error
          );
          // The error is already handled in refreshTokenWithQueue
          // which calls handleAuthenticationError and showSessionExpiredNotification
        });
      }
    });
  }

  private async handleNetworkErrorAndRetry<T>(
    url: string,
    options: RequestOptions,
    error: NetworkError | CorsError | TimeoutError
  ): Promise<ApiResponse<T>> {
    // Don't retry if this is already a retry
    if (options.isRetry) {
      console.error('[HttpClient] Network error persists after retry');

      // Show appropriate notification based on error type
      if (error instanceof CorsError) {
        showCorsErrorNotification();
        // CORS errors often indicate authentication issues, so redirect to login
        if (!isOnAuthPage()) {
          useAuthStore.getState().handleAuthenticationError();
          setTimeout(() => {
            navigateToLogin(true);
          }, 2000); // Give user time to see the notification
        }
      } else if (error instanceof TimeoutError) {
        showTimeoutErrorNotification();
      } else {
        showNetworkErrorNotification(error.message);
      }

      throw error;
    }

    // Retry with exponential backoff
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
      return await this.request<T>(url, { ...options, isRetry: true });
    } catch (retryError) {
      // If retry also fails, handle as above
      if (
        retryError instanceof NetworkError ||
        retryError instanceof CorsError ||
        retryError instanceof TimeoutError
      ) {
        return this.handleNetworkErrorAndRetry<T>(url, options, retryError);
      }
      throw retryError;
    }
  }

  private async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeout: number
  ): Promise<Response> {
    const controller = new AbortController();
    // Track this controller for potential cancellation
    this.activeControllers.add(controller);

    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      // Remove controller after successful completion
      this.activeControllers.delete(controller);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      // Remove controller after error
      this.activeControllers.delete(controller);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new TimeoutError(
          i18n.t('errors.network.requestTimeout'),
          timeout
        );
      }

      // Detect CORS errors (usually TypeError with specific message)
      if (error instanceof TypeError) {
        const errorMessage = error.message.toLowerCase();
        if (
          errorMessage.includes('cors') ||
          errorMessage.includes('cross-origin') ||
          errorMessage.includes('network') ||
          errorMessage.includes('fetch')
        ) {
          throw new CorsError(i18n.t('errors.network.corsError'));
        }
      }

      // Wrap other network errors
      if (error instanceof Error) {
        throw new NetworkError(
          i18n.t('errors.network.connectionFailed'),
          error
        );
      }

      throw error;
    }
  }

  cancelAllRequests(): void {
    // Cancel all active fetch requests
    this.activeControllers.forEach(controller => {
      controller.abort();
    });
    this.activeControllers.clear();

    // Clear the request queue
    const queuedRequests = [...this.requestQueue];
    this.requestQueue = [];

    // Reject all queued requests
    queuedRequests.forEach(request => {
      request.reject(new Error('Request cancelled due to logout'));
    });

    // Reset refresh state
    this.isRefreshing = false;

    // Reset session expired flag to allow showing notification again after re-login
    this.hasShownSessionExpired = false;
  }

  protected getHeaders(
    customHeaders?: HeadersInit,
    method?: string
  ): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json, text/plain, */*',
      ...(customHeaders as Record<string, string>),
    };

    // Remove Content-Type if explicitly set to undefined (for FormData uploads)
    if (
      customHeaders &&
      'Content-Type' in customHeaders &&
      (customHeaders as Record<string, string | undefined>)['Content-Type'] ===
        undefined
    ) {
      delete headers['Content-Type'];
    }

    // Auth token is now sent via HttpOnly cookie (credentials: 'include')
    // No Authorization header needed for browser requests

    // Add CSRF token for state-changing requests
    // Prefer in-memory token (fetched from /auth/csrf/) over cookie-based token
    // because csrftoken cookie on api.agentlinc.ai is not readable cross-subdomain
    const csrfToken = this.csrfToken || getCsrfToken();
    if (
      csrfToken &&
      method &&
      ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method.toUpperCase())
    ) {
      headers['X-CSRFToken'] = csrfToken;
    }

    // Set X-User-Id header if userId is available (GTC-compatible)
    // Check both in-memory userId and localStorage as fallback
    const userId =
      this.userId || localStorage.getItem(this.USER_ID_STORAGE_KEY);

    headers['X-tenant-slug'] = 'gamuda-engineering';

    if (userId) {
      headers['X-User-Id'] = userId;
      headers['user-id'] = userId;
      headers['x-user-id'] = userId; // Add lowercase variant

      // Update in-memory userId if it was retrieved from localStorage
      if (!this.userId && userId) {
        this.userId = userId;
      }
    }

    return headers;
  }

  async request<T>(
    url: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const {
      headers,
      timeout = REQUEST_CONFIG.TIMEOUT,
      isRetry = false,
      ...restOptions
    } = options;

    const requestStartTime = now();
    const method = restOptions.method || 'GET';

    // Check if we should preemptively refresh the token
    if (!isRetry && this.shouldRefreshToken()) {
      try {
        await this.refreshTokenWithQueue();
      } catch (error) {
        console.error('[HttpClient] Preemptive refresh failed:', error);
        // Continue with the request anyway - it might still work
      }
    }

    try {
      const response = await this.fetchWithTimeout(
        url,
        {
          ...restOptions,
          headers: this.getHeaders(headers, method),
        },
        timeout
      );

      const duration = now() - requestStartTime;
      this.logApiRequest({
        method,
        url,
        status: response.status,
        duration,
        requestHeaders: this.getHeaders(headers, method) as Record<
          string,
          string
        >,
        requestBody: restOptions.body,
        type: 'http',
      });

      if (!response.ok) {
        // Handle 401 specifically
        if (response.status === 401 && !isRetry) {
          return this.handle401AndRetry<T>(url, options);
        }

        // Try to parse error response as JSON, but handle HTML responses gracefully
        let errorData: Record<string, unknown> = {};
        let errorMessage = response.statusText;

        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
            errorMessage =
              (errorData.message as string) ||
              (errorData.detail as string) ||
              response.statusText;
          } else {
            // If it's not JSON (likely HTML), read as text for debugging
            const textResponse = await response.text();
            console.error('[HttpClient] Non-JSON error response:', {
              status: response.status,
              statusText: response.statusText,
              contentType,
              url,
              responsePreview: textResponse.substring(0, 200) + '...',
            });
            errorMessage = `Server returned ${response.status} ${response.statusText}`;
          }
        } catch (parseError) {
          console.error(
            '[HttpClient] Failed to parse error response:',
            parseError
          );
          errorMessage = `Server returned ${response.status} ${response.statusText}`;
        }

        const error = createApiError(response.status, errorMessage, errorData);
        throw error;
      }

      // Try to parse successful response as JSON
      let data: T;
      try {
        // For DELETE requests with 204 No Content, don't try to parse JSON
        if (response.status === 204) {
          // 204 No Content should return undefined
          return { data: undefined as T };
        } else if (
          options.method === 'DELETE' &&
          response.status >= 200 &&
          response.status < 300
        ) {
          // For other successful DELETE responses, try to read as text first
          const text = await response.text();
          if (text.trim() === '') {
            // Empty response is acceptable for DELETE operations
            return { data: undefined as T };
          }
          // If there's content, try to parse it as JSON
          // Fix: integers with 16+ digits exceed Number.MAX_SAFE_INTEGER and are
          // silently corrupted by JSON.parse. Convert them to strings first.
          data = JSON.parse(safeBigInts(text));
        } else {
          // Fix: response.json() has the same precision loss — read as text first.
          const rawText = await response.text();
          data = JSON.parse(safeBigInts(rawText));
        }
      } catch (parseError) {
        console.error(
          '[HttpClient] Failed to parse JSON response:',
          parseError
        );

        // For non-DELETE methods, try to get more info about the response
        if (options.method !== 'DELETE') {
          try {
            // Clone the response to read as text (if not already consumed)
            const responseClone = response.clone();
            const responseText = await responseClone.text();
            if (
              responseText.trim().toLowerCase().startsWith('<!doctype') ||
              responseText.trim().toLowerCase().startsWith('<html')
            ) {
              throw new Error(
                `Server returned HTML instead of JSON. Status: ${response.status} ${response.statusText}`
              );
            }
          } catch {
            // Response already consumed, continue with generic error
          }
        }

        throw new Error(i18n.t('errors.general.invalidResponse'));
      }

      return { data };
    } catch (error) {
      console.error('[HttpClient] Request failed:', error);

      const duration = now() - requestStartTime;
      this.logApiRequest({
        method,
        url,
        duration,
        requestHeaders: this.getHeaders(headers, method) as Record<
          string,
          string
        >,
        requestBody: restOptions.body,
        error: error instanceof Error ? error.message : String(error),
        type: 'http',
      });

      // Handle network errors with retry logic
      if (
        !isRetry &&
        (error instanceof NetworkError ||
          error instanceof CorsError ||
          error instanceof TimeoutError)
      ) {
        return this.handleNetworkErrorAndRetry<T>(url, options, error);
      }

      // If it's a 401 error that we haven't handled yet
      if (error instanceof AuthenticationError && !isRetry) {
        return this.handle401AndRetry<T>(url, options);
      }

      if (error instanceof Error) {
        throw error;
      }
      return {
        error:
          error instanceof Error
            ? error.message
            : i18n.t('errors.general.unknownError'),
      };
    }
  }

  private logApiRequest(_logData: {
    method: string;
    url: string;
    status?: number;
    duration?: number;
    requestHeaders?: Record<string, string>;
    requestBody?: unknown;
    responseHeaders?: Record<string, string>;
    responseBody?: unknown;
    error?: string;
    type: 'http' | 'streaming';
  }): void {
    // API logging removed - DevTools has been removed from the codebase
  }

  async get<T>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...options, method: 'GET' });
  }

  async post<T>(
    url: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async patch<T>(
    url: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(
    url: string,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...options, method: 'DELETE' });
  }

  // Special method for SSE streaming
  createEventSource(url: string, body?: unknown): EventSource {
    if (!API_CONFIG.ENABLE_STREAMING) {
      throw new Error(i18n.t('errors.chat.streamingDisabled'));
    }

    // For SSE with POST, we need to use a different approach
    // Since EventSource only supports GET, we'll need to handle this differently
    // This is a placeholder - actual implementation would depend on backend support
    const params = new URLSearchParams({
      data: JSON.stringify(body),
    });

    const eventSourceUrl = `${url}?${params.toString()}`;
    const eventSource = new EventSource(eventSourceUrl);

    // Note: EventSource doesn't support custom headers or credentials
    // Auth is handled via HttpOnly cookies for same-origin requests

    return eventSource;
  }

  // Streaming implementation with file upload support
  async streamPostWithFiles(
    url: string,
    data: { text?: string; files?: File[]; [key: string]: unknown },
    onMessage: (data: unknown) => void,
    onError?: (error: Error) => void,
    isRetry = false,
    options?: {
      fileFieldName?: string; // Custom field name for files (default: 'file_')
      jsonFieldName?: string; // Custom field name for JSON data (default: 'data')
      customHeaders?: HeadersInit; // Custom headers to include in the request
    }
  ): Promise<void> {
    // Check if we should preemptively refresh the token
    if (!isRetry && this.shouldRefreshToken()) {
      try {
        await this.refreshTokenWithQueue();
      } catch (error) {
        console.error(
          '[HttpClient] Preemptive refresh failed for stream:',
          error
        );
      }
    }

    try {
      let body: FormData | string;
      let contentType: string | undefined;

      // Check if we have GCS file URLs (for retry functionality)
      const fileUrlsValue = data.file_urls as string | undefined;
      const hasFileUrls =
        typeof fileUrlsValue === 'string' && fileUrlsValue.length > 0;

      // Use FormData if files are present OR if file_urls are present, otherwise use JSON
      if (data.files && data.files.length > 0) {
        const formData = new FormData();

        // Add text data as JSON string
        const { files, ...jsonData } = data;
        const jsonFieldName = options?.jsonFieldName || 'data';
        // Validate files before processing
        if (!files || files.length === 0) {
          throw new Error('No files provided for multimodal upload');
        }

        for (const file of files) {
          if (typeof file !== 'object' || !file) {
            throw new Error(
              `Invalid file object: expected File or Blob, got ${typeof file}`
            );
          }
        }

        formData.append(jsonFieldName, JSON.stringify(jsonData));

        // Add files with enhanced validation
        const fileFieldName = options?.fileFieldName || 'file_';

        for (let index = 0; index < files.length; index++) {
          const file = files[index];
          // If fileFieldName ends with underscore, add index (e.g., file_0, file_1)
          // Otherwise use the name as-is for all files (e.g., files, files, files)
          const fieldName = fileFieldName.endsWith('_')
            ? `${fileFieldName}${index}`
            : fileFieldName;
          // Verify the file has actual content
          if (file.size === 0) {
            console.error(
              `[HttpClient] ERROR: File "${file.name}" has zero size!`
            );
            throw new Error(`File "${file.name}" is empty`);
          }

          // Try to ensure file is readable before appending
          try {
            // Read the file content as ArrayBuffer to ensure it's loaded
            const arrayBuffer = await new Promise<ArrayBuffer>(
              (resolve, reject) => {
                const reader = new FileReader();

                reader.onload = () => {
                  const result = reader.result as ArrayBuffer;
                  resolve(result);
                };

                reader.onerror = () => {
                  console.error(
                    `[HttpClient] FileReader error for "${file.name}":`,
                    reader.error
                  );
                  reject(new Error(`Failed to read file "${file.name}"`));
                };

                reader.readAsArrayBuffer(file);
              }
            );

            // Create a new Blob from the ArrayBuffer
            const blob = new Blob([arrayBuffer], { type: file.type });
            // Create a new File from the blob
            const newFile = new File([blob], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            });
            // Append the recreated file
            formData.append(fieldName, newFile, newFile.name);
          } catch (error) {
            console.error(
              `[HttpClient] Error processing file "${file.name}":`,
              error
            );
            throw new Error(`Failed to process file "${file.name}": ${error}`);
          }
        }

        body = formData;
        // Don't set Content-Type header - let browser set it with boundary
        contentType = undefined;
      } else if (hasFileUrls) {
        // Handle GCS file URLs (for retry functionality when local files are gone)
        // This uses FormData to match the multimodal endpoint expectation
        const formData = new FormData();
        const { _file_urls, ...jsonData } = data;
        const jsonFieldName = options?.jsonFieldName || 'data';
        // Add JSON data (question and config)
        formData.append(jsonFieldName, JSON.stringify(jsonData));

        // Add file_urls as a form field (comma-separated GCS URLs)
        formData.append('file_urls', fileUrlsValue);

        body = formData;
        // Don't set Content-Type header - let browser set it with boundary
        contentType = undefined;
      } else {
        body = JSON.stringify(data);
        contentType = 'application/json';
      }

      // Build headers - exclude Content-Type for FormData
      const baseHeaders = this.getHeaders(options?.customHeaders || {}, 'POST');
      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
      };

      // Copy headers except Content-Type when using FormData
      Object.entries(baseHeaders).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'content-type' || contentType) {
          headers[key] = value as string;
        }
      });

      // Only set Content-Type for JSON requests
      if (contentType) {
        headers['Content-Type'] = contentType;
      }
      const fetchOptions = {
        method: 'POST',
        headers,
        body,
        credentials: 'include' as RequestCredentials,
      };
      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        // Handle 401 for streaming
        if (response.status === 401 && !isRetry) {
          await this.refreshTokenWithQueue();
          // Retry the stream with same options (including custom headers)
          return this.streamPostWithFiles(
            url,
            data,
            onMessage,
            onError,
            true,
            options
          );
        }
        throw createApiError(response.status, response.statusText);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error(i18n.t('errors.chat.noResponseBody'));
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              let dataContent = line.slice(6); // Remove first "data: "

              // Check if there's a double "data: " prefix (new format)
              if (dataContent.startsWith('data: ')) {
                dataContent = dataContent.slice(6); // Remove second "data: "
              }

              // Skip empty data
              if (!dataContent.trim()) continue;

              // Fix: document_id values from RAG sources are large integers that exceed
              // Number.MAX_SAFE_INTEGER (2^53-1). Preserve them as strings before parsing.
              const data = JSON.parse(safeBigInts(dataContent));
              onMessage(data);
            } catch (e) {
              console.error('Failed to parse SSE data:', e, 'Line:', line);
            }
          }
        }
      }
    } catch (error) {
      console.error('[HttpClient] Stream error:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const enhancedError = new Error(
        `Failed to upload files: ${errorMessage}`
      );

      if (onError) {
        onError(enhancedError);
      } else {
        throw enhancedError;
      }
    }
  }

  // Alternative streaming implementation using fetch
  async streamPost(
    url: string,
    body: unknown,
    onMessage: (data: unknown) => void,
    onError?: (error: Error) => void,
    isRetry = false
  ): Promise<void> {
    // Check if we should preemptively refresh the token
    if (!isRetry && this.shouldRefreshToken()) {
      try {
        await this.refreshTokenWithQueue();
      } catch (error) {
        console.error(
          '[HttpClient] Preemptive refresh failed for stream:',
          error
        );
      }
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...this.getHeaders({}, 'POST'),
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // Handle 401 for streaming
        if (response.status === 401 && !isRetry) {
          await this.refreshTokenWithQueue();
          // Retry the stream
          return this.streamPost(url, body, onMessage, onError, true);
        }
        throw createApiError(response.status, response.statusText);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error(i18n.t('errors.chat.noResponseBody'));
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              let dataContent = line.slice(6); // Remove first "data: "

              // Check if there's a double "data: " prefix (new format)
              if (dataContent.startsWith('data: ')) {
                dataContent = dataContent.slice(6); // Remove second "data: "
              }

              // Skip empty data
              if (!dataContent.trim()) continue;

              // Fix: document_id values from RAG sources are large integers that exceed
              // Number.MAX_SAFE_INTEGER (2^53-1). Preserve them as strings before parsing.
              const data = JSON.parse(safeBigInts(dataContent));
              onMessage(data);
            } catch (e) {
              console.error('Failed to parse SSE data:', e, 'Line:', line);
            }
          }
        }
      }
    } catch (error) {
      if (onError) {
        onError(error as Error);
      } else {
        throw error;
      }
    }
  }
}

export const httpClient = new HttpClient();
